import { toastError } from 'admination-react'
import { useContext, useId, useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import { AutosaveContext } from '@/core/components/form/AutosaveBoundary'
import { Autosave } from '@/core/lib/autosave'
import { assertWriteIdentity, captureWriteIdentity, RequestTimeoutError } from '@/core/api/http'
import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { friendlyErrorMessage } from '@/core/lib/dbError'

const saves = new Map<string, Autosave<unknown, unknown>>()
type Options<T> = { form: string; record?: string; recover?: (value: T) => void; label?: string }

export function useAutosave<T, R = void>(values: T, ready: boolean, save: (v: T, previous: T | undefined, assertCurrent: () => void) => R, delay = 250, options?: Options<T>) {
  const boundary = useContext(AutosaveContext)
  const localId = useId()
  const scope = currentAutosaveScope()
  const key = JSON.stringify([scope, options?.record ?? boundary?.identity ?? localId, options?.form ?? localId])
  let existing = saves.get(key)
  if (!existing) {
    const identity = captureWriteIdentity()
    existing = new Autosave<unknown, unknown>(() => undefined, () => {
      try { assertWriteIdentity(identity); return true } catch { return false }
    }, () => {})
    saves.set(key, existing)
  }
  const controller = existing as Autosave<T, Promise<R>>
  const label = [...new Set([boundary?.label, options?.label || "Formular"].filter(Boolean))].join(' - ')
  const error = (e: unknown) => toastError(`${label}: ${friendlyErrorMessage(e instanceof TypeError && !(e instanceof RequestTimeoutError) ? null : e, "Die Änderungen konnten nicht gespeichert werden. Bitte öffnen Sie das Formular erneut.")}`)
  if (ready) controller.bind(async (value, baseline) => {
    const identity = captureWriteIdentity()
    const result = await save(value, baseline, () => assertWriteIdentity(identity))
    assertWriteIdentity(identity)
    return result
  }, error)
  const status = useSyncExternalStore(controller.subscribe, controller.snapshot, controller.snapshot)
  const previous = useRef<{ controller: Autosave<T, Promise<R>>; value: T } | null>(null)
  const opening = useRef<{ controller: Autosave<T, Promise<R>>; value: T | undefined } | null>(null)
  if (opening.current?.controller !== controller) opening.current = { controller, value: controller.recovery() }
  const recover = useRef(options?.recover)
  recover.current = options?.recover

  useLayoutEffect(() => {
    boundary?.register(key, controller)
  }, [boundary, key, controller])

  useLayoutEffect(() => {
    if (!ready) return
    if (previous.current?.controller !== controller) {
      const pending = controller.recovery() ?? opening.current?.value
      if (pending === undefined) controller.initialize(values)
      previous.current = { controller, value: pending ?? values }
      if (pending !== undefined) {
        recover.current?.(pending)
        if (controller.snapshot() === 'saving') void controller.flush()?.catch(() => {})
      }
      return
    }
    if (Object.is(previous.current.value, values) || JSON.stringify(previous.current.value) === JSON.stringify(values)) return
    previous.current.value = values
    controller.schedule(values, delay)
  }, [values, ready, controller, delay])

  useLayoutEffect(() => () => { void controller.flush()?.catch(() => {}) }, [controller])
  const beginLoad = () => {
    opening.current = { controller, value: controller.recovery() }
    previous.current = null
  }
  return { flush: controller.flush, status, error: controller.error(), beginLoad }
}
